import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
	TVatCorrectionsStore,
	TVatCorrectionsResponsePayload,
	TVatCorrectionsRequestPayload,
	TVatCorrectionsFilterResponsePayload,
	TCurrenciesResponsePayload,
	TSystemTimeResponsePayload,
	TLoadEUCountriesResponsePayload,
	TExchangeRateRequestPayload,
	TExchangeRateResponsePayload,
	TVatCorrectionsSubmitRequestPayload,
	TGbpExchangeRateResponsePayload,
	TVatCorrectionsDownloadRequest,
	TOrderAvailableCorrectionAmountResponsePayload,
	TOrderAvailableCorrectionAmountRequest,
} from './types';

export const initialState: TVatCorrectionsStore = {
	vatCorrections: null,
	vatCorrectionsLoading: false,
	vatCorrectionsFilters: null,
	vatCorrectionsFiltersLoading: false,
	systemTime: null,
	systemTimeLoading: false,
	currencies: null,
	currenciesLoading: false,
	euCountries: null,
	euCountriesLoading: false,
	exchangeRate: null,
	exchangeRateLoading: false,
	gbpExchangeRate: null,
	gbpExchangeRateLoading: false,
	vatCorrectionSubmitLoading: false,
	vatCorrectionSubmitError: '',
	vatCorrectionDownloadLoader: false,
	orderAvailableCorrectionAmount: null,
	orderAvailableCorrectionAmountLoading: false,
	orderAvailableCorrectionAmountError: '',
};

const corrections = createSlice({
	name: '@@corrections',
	initialState,
	reducers: {
		getVatCorrectionsRequest: (
			state: TVatCorrectionsStore,
			action: PayloadAction<TVatCorrectionsRequestPayload>,
		) => {
			const vatCorrectionsState = state;
			vatCorrectionsState.vatCorrectionsLoading = true;
		},
		getVatCorrectionsSuccess: (
			state: TVatCorrectionsStore,
			action: PayloadAction<TVatCorrectionsResponsePayload>,
		) => {
			const { payload } = action;
			const vatCorrectionsState = state;
			vatCorrectionsState.vatCorrectionsLoading = false;
			vatCorrectionsState.vatCorrections = payload;
		},
		getSystemTimeRequest: (state: TVatCorrectionsStore) => {
			const vatCorrectionsState = state;
			vatCorrectionsState.systemTimeLoading = true;
		},
		getSystemTimeSuccess: (
			state: TVatCorrectionsStore,
			action: PayloadAction<TSystemTimeResponsePayload>,
		) => {
			const { payload } = action;
			const vatCorrectionsState = state;
			vatCorrectionsState.systemTimeLoading = false;
			vatCorrectionsState.systemTime = payload;
		},
		getCurrenciesRequest: (state: TVatCorrectionsStore) => {
			const vatCorrectionsState = state;
			vatCorrectionsState.currenciesLoading = true;
		},
		getCurrenciesSuccess: (
			state: TVatCorrectionsStore,
			action: PayloadAction<TCurrenciesResponsePayload>,
		) => {
			const { payload } = action;
			const vatCorrectionsState = state;
			vatCorrectionsState.currenciesLoading = false;
			vatCorrectionsState.currencies = payload;
		},
		getEUCountriesRequest: (state: TVatCorrectionsStore) => {
			const vatCorrectionsState = state;
			vatCorrectionsState.euCountriesLoading = true;
		},
		getEUCountriesSuccess: (
			state: TVatCorrectionsStore,
			action: PayloadAction<TLoadEUCountriesResponsePayload>,
		) => {
			const { payload } = action;
			const vatCorrectionsState = state;
			vatCorrectionsState.euCountriesLoading = false;
			vatCorrectionsState.euCountries = payload;
		},
		getExchangeRateRequest: (
			state: TVatCorrectionsStore,
			action: PayloadAction<TExchangeRateRequestPayload>,
		) => {
			const vatCorrectionsState = state;
			vatCorrectionsState.exchangeRateLoading = true;
		},
		getExchangeRateSuccess: (
			state: TVatCorrectionsStore,
			action: PayloadAction<TExchangeRateResponsePayload>,
		) => {
			const { payload } = action;
			const vatCorrectionsState = state;
			vatCorrectionsState.exchangeRateLoading = false;
			vatCorrectionsState.exchangeRate = payload;
		},
		getGbpExchangeRateRequest: (
			state: TVatCorrectionsStore,
			action: PayloadAction<TExchangeRateRequestPayload>,
		) => {
			const vatCorrectionsState = state;
			vatCorrectionsState.gbpExchangeRateLoading = true;
		},
		getGbpExchangeRateSuccess: (
			state: TVatCorrectionsStore,
			action: PayloadAction<TGbpExchangeRateResponsePayload>,
		) => {
			const { payload } = action;
			const vatCorrectionsState = state;
			vatCorrectionsState.gbpExchangeRateLoading = false;
			vatCorrectionsState.gbpExchangeRate = payload;
		},
		vatCorrectionSubmitRequest: (
			state: TVatCorrectionsStore,
			action: PayloadAction<TVatCorrectionsSubmitRequestPayload>,
		) => {
			const vatCorrectionsState = state;
			vatCorrectionsState.vatCorrectionSubmitLoading = true;
			vatCorrectionsState.vatCorrectionSubmitError = '';
		},
		vatCorrectionSubmitSuccess: (state: TVatCorrectionsStore) => {
			const vatCorrectionsState = state;
			vatCorrectionsState.vatCorrectionSubmitLoading = false;
			vatCorrectionsState.vatCorrectionSubmitError = '';
		},
		vatCorrectionSubmitError: (state: TVatCorrectionsStore, action: PayloadAction<string>) => {
			const { payload } = action;
			const vatCorrectionsState = state;
			vatCorrectionsState.vatCorrectionSubmitLoading = false;
			vatCorrectionsState.vatCorrectionSubmitError = payload;
		},
		vatCorrectionDownloadRequest: (
			state: TVatCorrectionsStore,
			action: PayloadAction<TVatCorrectionsDownloadRequest>,
		) => {
			const vatCorrectionsState = state;
			vatCorrectionsState.vatCorrectionDownloadLoader = true;
		},
		vatCorrectionDownloadSuccess: (state: TVatCorrectionsStore) => {
			const vatCorrectionsState = state;
			vatCorrectionsState.vatCorrectionDownloadLoader = false;
		},
		getOrderAvailableCorrectionAmountRequest: (
			state: TVatCorrectionsStore,
			action: PayloadAction<TOrderAvailableCorrectionAmountRequest>,
		) => {
			const vatCorrectionsState = state;
			vatCorrectionsState.orderAvailableCorrectionAmountLoading = true;
			vatCorrectionsState.orderAvailableCorrectionAmountError = '';
		},
		getOrderAvailableCorrectionAmountSuccess: (
			state: TVatCorrectionsStore,
			action: PayloadAction<TOrderAvailableCorrectionAmountResponsePayload>,
		) => {
			const { payload } = action;
			const vatCorrectionsState = state;
			vatCorrectionsState.orderAvailableCorrectionAmountLoading = false;
			vatCorrectionsState.orderAvailableCorrectionAmount = payload;
			vatCorrectionsState.orderAvailableCorrectionAmountError = '';
		},
		setOrderAvailableCorrectionAmountError: (
			state: TVatCorrectionsStore,
			action: PayloadAction<string>,
		) => {
			const { payload } = action;
			const vatCorrectionsState = state;
			vatCorrectionsState.orderAvailableCorrectionAmountLoading = false;
			vatCorrectionsState.orderAvailableCorrectionAmountError = payload;
		},

		correctionsInitState: () => initialState,
	},
});

export default corrections.reducer;
export const {
	getVatCorrectionsRequest,
	getVatCorrectionsSuccess,
	correctionsInitState,
	getSystemTimeRequest,
	getSystemTimeSuccess,
	getCurrenciesRequest,
	getCurrenciesSuccess,
	getEUCountriesRequest,
	getEUCountriesSuccess,
	getExchangeRateRequest,
	getExchangeRateSuccess,
	getGbpExchangeRateRequest,
	getGbpExchangeRateSuccess,
	vatCorrectionSubmitRequest,
	vatCorrectionSubmitSuccess,
	vatCorrectionSubmitError,
	vatCorrectionDownloadRequest,
	vatCorrectionDownloadSuccess,
	getOrderAvailableCorrectionAmountRequest,
	getOrderAvailableCorrectionAmountSuccess,
	setOrderAvailableCorrectionAmountError,
} = corrections.actions;
